import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { optionLanguage } from '@/constants/options';
import { capitalizeFirstLetter } from '@/helpers/string';
import useTranslate from '@/hooks/useTranslate';
import languageRuleSlice, { initLanguageRule, selectedLanguageRuleSelector } from '@/redux/slice/languageRule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { ILanguageRuleAppearance } from '@/types/languageRule.type';
import { Button, TextField } from '@shopify/polaris';
import { LanguageTranslateIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../CustomTable';
import { EditAppearanceStyled } from './styled';

type LanguageAppearanceField =
  | 'shippingMethodLabel'
  | 'prepareStep'
  | 'shipStep'
  | 'deliveryStep'
  | 'zipcodeLabel'
  | 'zipcodeInputPlaceholder'
  | 'zipcodeAvailableText'
  | 'zipcodeNotAvailableText'
  | 'submitButton'
  | 'countdown';

const EditAppearance = () => {
  const dispatch = useDispatch();
  const { translateText, isShowTranslate } = useTranslate();
  const currentLanguageRule = useSelector(selectedLanguageRuleSelector);
  const dataSettings = useSelector(dataSettingsSelector);

  const [isLoading, setIsLoading] = useState(false);

  const dataRow = useMemo(() => {
    return [
      {
        id: '-1',
        label: 'Banner header',
        shippingMethodLabel: dataSettings.shippingMethodLabel,
        field: 'shippingMethodLabel',
      },
      {
        id: '0',
        group: 'Timeline graphic',
      },
      {
        id: '1',
        label: 'Stage 1',
        timeline_order_step_title: dataSettings.timeline_order_step_title || ' ',
        timeline_order_step_description: dataSettings.timeline_order_step_description || ' ',
        field: 'prepareStep',
      },
      {
        id: '2',
        label: 'Stage 2',
        timeline_ship_step_title: dataSettings.timeline_ship_step_title || ' ',
        timeline_ship_step_description: dataSettings.timeline_ship_step_description || ' ',
        field: 'shipStep',
      },
      {
        id: '3',
        label: 'Stage 3',
        timeline_delivery_step_title: dataSettings.timeline_delivery_step_title || ' ',
        timeline_delivery_step_description: dataSettings.timeline_delivery_step_description || ' ',
        field: 'deliveryStep',
      },
      {
        id: '4',
        group: 'Zipcode checker',
      },
      {
        id: '5',
        label: 'Label',
        zipcodeInputLabel: dataSettings?.zipcodeSetting?.inputLabel || ' ',
        field: 'zipcodeLabel',
      },
      {
        id: '6',
        label: 'Input indicator',
        zipcodeInputPlaceholder: dataSettings?.zipcodeSetting?.inputPlaceholder || ' ',
        field: 'zipcodeInputPlaceholder',
      },
      {
        id: '7',
        label: 'Text if zipcode is valid',
        zipcodeAvailableText: dataSettings?.zipcodeSetting?.zipcodeAvailableText || ' ',
        field: 'zipcodeAvailableText',
      },
      {
        id: '8',
        label: 'Text if zipcode is invalid',
        zipcodeNotAvailableText: dataSettings?.zipcodeSetting?.zipcodeNotAvailableText || ' ',
        field: 'zipcodeNotAvailableText',
      },
      {
        id: '9',
        label: 'Button',
        submitButton: dataSettings?.zipcodeSetting?.submitButton || ' ',
        field: 'submitButton',
      },
      {
        id: '10',
        group: 'Countdown',
      },
      {
        id: '11',
        label: 'Timer format',
        days: dataSettings?.days || ' ',
        hours: dataSettings?.hours || ' ',
        minutes: dataSettings?.minutes || ' ',
        seconds: dataSettings?.seconds || ' ',
        field: 'countdown',
      },
    ];
  }, [
    dataSettings?.days,
    dataSettings?.hours,
    dataSettings?.minutes,
    dataSettings?.seconds,
    dataSettings.shippingMethodLabel,
    dataSettings.timeline_delivery_step_description,
    dataSettings.timeline_delivery_step_title,
    dataSettings.timeline_order_step_description,
    dataSettings.timeline_order_step_title,
    dataSettings.timeline_ship_step_description,
    dataSettings.timeline_ship_step_title,
    dataSettings?.zipcodeSetting?.inputLabel,
    dataSettings?.zipcodeSetting?.inputPlaceholder,
    dataSettings?.zipcodeSetting?.submitButton,
    dataSettings?.zipcodeSetting?.zipcodeAvailableText,
    dataSettings?.zipcodeSetting?.zipcodeNotAvailableText,
  ]);

  const handleChangeAppearance = useCallback(
    (field: string, value: string) => {
      if (!currentLanguageRule.appearanceContent) return;
      const temp = { ...currentLanguageRule.appearanceContent, [field]: value };
      dispatch(languageRuleSlice.actions.handleChangeSelectedRule({ key: 'appearanceContent', value: temp }));
    },
    [dispatch, currentLanguageRule.appearanceContent],
  );

  const renderStepTimeline = useCallback((title: string, description: string) => {
    return (
      <td>
        <div className="d-flex flex-column">
          <BoldSmallText>Title</BoldSmallText>
          <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
            <RegularText>{title}</RegularText>
          </div>
          <div className="mt-8 d-flex flex-column">
            <BoldSmallText>Tooltip</BoldSmallText>
            <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
              <RegularText>{description}</RegularText>
            </div>
          </div>
        </div>
      </td>
    );
  }, []);

  const renderTextFieldWithLabel = useCallback(
    (label: string, field: string, value: string) => {
      return (
        <div>
          <RegularText>{label}</RegularText>
          <TextField
            label=""
            labelHidden
            autoComplete="off"
            value={value}
            onChange={(value) => handleChangeAppearance(field, value)}
          />
        </div>
      );
    },
    [handleChangeAppearance],
  );

  const renderActionTimeline = useCallback(
    (type: 'order' | 'ship' | 'delivery') => {
      const capitalizeType = capitalizeFirstLetter(type);
      const title =
        currentLanguageRule.appearanceContent?.[`timeline${capitalizeType}StepTitle` as keyof ILanguageRuleAppearance];
      const description =
        currentLanguageRule.appearanceContent?.[`timeline${capitalizeType}StepDescription` as keyof ILanguageRuleAppearance];
      const fieldTitle = `timeline${capitalizeType}StepTitle`;
      const fieldDescription = `timeline${capitalizeType}StepDescription`;
      return (
        <div className="d-flex flex-column gap-8">
          {renderTextFieldWithLabel('Title', fieldTitle, title || '')}
          {renderTextFieldWithLabel('Tooltip', fieldDescription, description || '')}
        </div>
      );
    },
    [currentLanguageRule.appearanceContent, renderTextFieldWithLabel],
  );

  const renderZipcodeTextField = useCallback(
    (field: string, value: string) => {
      return (
        <div style={{ padding: '8px 0' }}>
          <TextField
            label=""
            labelHidden
            autoComplete="off"
            value={value}
            onChange={(value) => handleChangeAppearance(field, value)}
          />
        </div>
      );
    },
    [handleChangeAppearance],
  );

  const handleTranslate = async () => {
    const translateData = { ...currentLanguageRule.appearanceContent };
    setIsLoading(true);
    const fieldsToTranslate: { key: keyof typeof translateData; source: string }[] = [
      { key: 'shippingMethodLabel', source: dataSettings?.shippingMethodLabel || '' },
      { key: 'timelineOrderStepTitle', source: dataSettings?.timeline_order_step_title },
      { key: 'timelineOrderStepDescription', source: dataSettings?.timeline_order_step_description },
      { key: 'timelineShipStepTitle', source: dataSettings?.timeline_ship_step_title },
      { key: 'timelineShipStepDescription', source: dataSettings?.timeline_ship_step_description },
      { key: 'timelineDeliveryStepTitle', source: dataSettings?.timeline_delivery_step_title },
      { key: 'timelineDeliveryStepDescription', source: dataSettings?.timeline_delivery_step_description },
      { key: 'inputLabel', source: dataSettings?.zipcodeSetting?.inputLabel },
      { key: 'inputPlaceholder', source: dataSettings?.zipcodeSetting?.inputPlaceholder },
      { key: 'zipcodeAvailableText', source: dataSettings?.zipcodeSetting?.zipcodeAvailableText },
      { key: 'zipcodeNotAvailableText', source: dataSettings?.zipcodeSetting?.zipcodeNotAvailableText },
      { key: 'submitButton', source: dataSettings?.zipcodeSetting?.submitButton },
      { key: 'days', source: dataSettings?.days },
      { key: 'hours', source: dataSettings?.hours },
      { key: 'minutes', source: dataSettings?.minutes },
      { key: 'seconds', source: dataSettings?.seconds },
    ];

    await Promise.all(
      fieldsToTranslate.map(async ({ key, source }) => {
        if (!translateData[key]) {
          translateData[key] = await translateText(source || '');
        }
      }),
    );

    dispatch(
      languageRuleSlice.actions.handleChangeSelectedRule({
        key: 'appearanceContent',
        value: { ...(currentLanguageRule.appearanceContent || initLanguageRule.appearanceContent), ...translateData },
      }),
    );

    setIsLoading(false);
  };

  const actionCells = useMemo(() => {
    return {
      shippingMethodLabel: (
        <TextField
          autoComplete="off"
          label=""
          value={currentLanguageRule.appearanceContent?.shippingMethodLabel}
          onChange={(value) => handleChangeAppearance('shippingMethodLabel', value)}
        />
      ),
      prepareStep: renderActionTimeline('order'),
      shipStep: renderActionTimeline('ship'),
      deliveryStep: renderActionTimeline('delivery'),

      zipcodeLabel: renderZipcodeTextField('inputLabel', currentLanguageRule.appearanceContent?.inputLabel || ''),
      zipcodeInputPlaceholder: renderZipcodeTextField(
        'inputPlaceholder',
        currentLanguageRule.appearanceContent?.inputPlaceholder || '',
      ),
      zipcodeAvailableText: renderZipcodeTextField(
        'zipcodeAvailableText',
        currentLanguageRule.appearanceContent?.zipcodeAvailableText || '',
      ),
      zipcodeNotAvailableText: renderZipcodeTextField(
        'zipcodeNotAvailableText',
        currentLanguageRule.appearanceContent?.zipcodeNotAvailableText || '',
      ),
      submitButton: renderZipcodeTextField('submitButton', currentLanguageRule.appearanceContent?.submitButton || ''),

      countdown: (
        <div className="d-flex flex-column gap-8">
          {renderTextFieldWithLabel('Day text', 'days', currentLanguageRule.appearanceContent?.days || '')}
          {renderTextFieldWithLabel('Hour text', 'hours', currentLanguageRule.appearanceContent?.hours || '')}
          {renderTextFieldWithLabel('Minute text', 'minutes', currentLanguageRule.appearanceContent?.minutes || '')}
          {renderTextFieldWithLabel('Second text', 'seconds', currentLanguageRule.appearanceContent?.seconds || '')}
        </div>
      ),
    };
  }, [
    currentLanguageRule.appearanceContent?.days,
    currentLanguageRule.appearanceContent?.hours,
    currentLanguageRule.appearanceContent?.inputLabel,
    currentLanguageRule.appearanceContent?.inputPlaceholder,
    currentLanguageRule.appearanceContent?.minutes,
    currentLanguageRule.appearanceContent?.seconds,
    currentLanguageRule.appearanceContent?.shippingMethodLabel,
    currentLanguageRule.appearanceContent?.submitButton,
    currentLanguageRule.appearanceContent?.zipcodeAvailableText,
    currentLanguageRule.appearanceContent?.zipcodeNotAvailableText,
    handleChangeAppearance,
    renderActionTimeline,
    renderTextFieldWithLabel,
    renderZipcodeTextField,
  ]);

  const rowMarkup = useMemo(() => {
    return dataRow.map(
      ({
        id,
        label,
        shippingMethodLabel,
        days,
        hours,
        minutes,
        seconds,
        zipcodeInputLabel,
        zipcodeInputPlaceholder,
        zipcodeAvailableText,
        zipcodeNotAvailableText,
        submitButton,
        group,
        timeline_delivery_step_description,
        timeline_order_step_title,
        timeline_ship_step_title,
        timeline_delivery_step_title,
        timeline_order_step_description,
        timeline_ship_step_description,
        field,
      }) => {
        if (group) {
          return (
            <tr id={id} key={id}>
              <td className="bg-gray" colSpan={3}>
                <BoldSmallText>{group}</BoldSmallText>
              </td>
            </tr>
          );
        }
        return (
          <tr id={id} key={id}>
            <td>{label}</td>
            {shippingMethodLabel ? <td>{shippingMethodLabel}</td> : null}
            {timeline_order_step_title || timeline_order_step_description
              ? renderStepTimeline(timeline_order_step_title, timeline_order_step_description)
              : null}

            {timeline_ship_step_description || timeline_ship_step_title
              ? renderStepTimeline(timeline_ship_step_title, timeline_ship_step_description)
              : null}

            {timeline_delivery_step_description || timeline_delivery_step_title
              ? renderStepTimeline(timeline_delivery_step_title, timeline_delivery_step_description)
              : null}

            {zipcodeInputLabel ? <td>{zipcodeInputLabel}</td> : null}
            {zipcodeInputPlaceholder ? <td>{zipcodeInputPlaceholder}</td> : null}
            {zipcodeAvailableText ? <td>{zipcodeAvailableText}</td> : null}
            {zipcodeNotAvailableText ? <td>{zipcodeNotAvailableText}</td> : null}
            {submitButton ? <td>{submitButton}</td> : null}

            {days || hours || minutes || seconds ? (
              <td>
                <div className="d-flex flex-column gap-8">
                  <div className="mt-8 d-flex flex-column">
                    <BoldSmallText>Day text</BoldSmallText>
                    <RegularText>{days}</RegularText>
                  </div>
                  <div className="mt-8 d-flex flex-column">
                    <BoldSmallText>Hour text</BoldSmallText>
                    <RegularText>{hours}</RegularText>
                  </div>
                  <div className="mt-8 d-flex flex-column">
                    <BoldSmallText>Minute text</BoldSmallText>
                    <RegularText>{minutes}</RegularText>
                  </div>
                  <div className="mt-8 d-flex flex-column">
                    <BoldSmallText>Second text</BoldSmallText>
                    <RegularText>{seconds}</RegularText>
                  </div>
                </div>
              </td>
            ) : null}

            <td>{actionCells[field as LanguageAppearanceField]}</td>
          </tr>
        );
      },
    );
  }, [actionCells, dataRow, renderStepTimeline]);

  const headings = [
    {
      title: 'Field',
      width: '15%',
    },
    {
      title: `Default language (${optionLanguage.find((option) => option.value === dataSettings?.date_locale)?.label})`,
      width: '30%',
    },
    {
      title: currentLanguageRule?.nameLanguage,
      width: '55%',
    },
  ];

  return (
    <EditAppearanceStyled>
      {isShowTranslate ? (
        <div className="d-flex justify-between align-center">
          <BoldText>Appearance content</BoldText>
          <Button icon={LanguageTranslateIcon} onClick={handleTranslate} loading={isLoading}>
            Auto-translate
          </Button>
        </div>
      ) : null}
      <div className="table-container">
        <CustomTable headings={headings} rowMarkup={rowMarkup} />
      </div>
    </EditAppearanceStyled>
  );
};

export default memo(EditAppearance);
